// Fix P (2026-10-13): las etapas de la carga en el panel. Las claves y los nombres son los mismos que usa el servidor (backend/etapas.py).
import { formatFecha, formatHoraInicio, siglaZona } from "@/lib/format";
import { t } from "@/lib/i18n";

export const ORDEN_ETAPAS = ["pick_chassis", "rail_pickup", "yard_stop", "delivery", "return_empty", "empty_pickup", "pickup_location", "return_loaded", "return_chassis"];
export const FLUJO_IMPORT = ["pick_chassis", "rail_pickup", "yard_stop", "delivery", "return_empty", "return_chassis"];
// Exportación: 1 Pickup Chassis · 2 Empty Container Pickup · 3 Pickup Location · 4 Return Loaded Container · 5 Return Chassis
export const FLUJO_EXPORT = ["pick_chassis", "empty_pickup", "pickup_location", "return_loaded", "return_chassis"];
export const esFlujoExport = (etapasOCarga) => (Array.isArray(etapasOCarga) ? etapasOCarga : etapasOCarga?.etapas || []).some((e) => e.clave === "empty_pickup" && e.activa);

// ubic: tipos de ubicación del catálogo que sirven para esa etapa (los mismos que valida el servidor).
// lugarObligatorio: sin lugar no se puede guardar la carga (la parada en yarda solo si está activa).
export const ETAPA = {
  pick_chassis: { get nombre() { return t("Pick up chassis"); }, ubic: ["alquiler_chasis", "yarda", "deposito_vacios", "riel"], get lugar() { return t("Ubicación"); }, get ref() { return t("Ej.: Chassis reservation"); }, lugarObligatorio: false },
  rail_pickup: { get nombre() { return t("Rail pickup"); }, ubic: ["riel"], get lugar() { return t("Ubicación del riel"); }, get ref() { return t("Ej.: Release #5478"); }, lugarObligatorio: true },
  yard_stop: { get nombre() { return t("Yard stop"); }, ubic: ["yarda"], get lugar() { return t("Yarda"); }, get ref() { return t("Ej.: dejar el equipo en la yarda"); }, lugarObligatorio: true },
  delivery: { get nombre() { return t("Delivery"); }, ubic: [], get lugar() { return t("Receiver (quien recibe)"); }, get ref() { return t("Ej.: DEL-1042"); }, lugarObligatorio: true },
  return_empty: { get nombre() { return t("Return empty container"); }, ubic: ["riel", "deposito_vacios", "yarda"], get lugar() { return t("Dónde se devuelve el vacío"); }, get ref() { return t("Ej.: Empty return reference"); }, lugarObligatorio: true },
  empty_pickup: { get nombre() { return t("Empty Container Pickup"); }, ubic: ["riel", "deposito_vacios", "yarda"], get lugar() { return t("Dónde se recoge el contenedor vacío"); }, get ref() { return t("Ej.: Booking #5478"); }, lugarObligatorio: true },
  pickup_location: { get nombre() { return t("Pickup Location"); }, ubic: [], get lugar() { return t("Pickup location (donde se carga)"); }, get ref() { return t("Ej.: PU-1042"); }, lugarObligatorio: true },
  return_loaded: { get nombre() { return t("Return Loaded Container"); }, ubic: ["riel", "deposito_vacios", "yarda"], get lugar() { return t("Dónde se devuelve el contenedor cargado"); }, get ref() { return t("Ej.: Booking #5478"); }, lugarObligatorio: true },
  return_chassis: { get nombre() { return t("Return chassis"); }, ubic: ["alquiler_chasis", "deposito_vacios", "yarda", "riel"], get lugar() { return t("Dónde se devuelve el chassis"); }, get ref() { return t("Ej.: número de chassis"); }, lugarObligatorio: false },
};

export const ZONAS = {
  get "America/Chicago"() { return t("Chicago (CT)"); },
  get "America/New_York"() { return t("Nueva York (ET)"); },
  get "America/Denver"() { return t("Denver (MT)"); },
  get "America/Los_Angeles"() { return t("Los Ángeles (PT)"); },
};

export const TAMANOS = ["20", "40", "40HC", "45", "53"];

export const ESTADO_ETAPA = {
  pendiente: { get label() { return t("Pendiente"); }, cls: "bg-surface-2 text-ink-2 border-line-strong" },
  en_lugar: { get label() { return t("En el lugar"); }, cls: "bg-navy-soft text-navy-2 border-[#C9D7E8]" },
  completada: { get label() { return t("Completada"); }, cls: "bg-ok-soft text-ok-ink border-ok-line" },
};

const CAMPOS = [
  "nombre", "ubicacion_id", "lugar", "es_yarda_propia", "calle", "ciudad", "estado_us", "zip",
  "fecha", "hora_desde", "hora_hasta", "dias", "referencia", "instrucciones", "chofer_id",
  "zona_horaria", "tipo_descarga",
];

export function etapaVacia(clave, fecha = "") {
  return {
    clave, activa: clave !== "yard_stop", nombre: ETAPA[clave].nombre,
    ubicacion_id: "", lugar: "", es_yarda_propia: false, calle: "", ciudad: "", estado_us: "", zip: "",
    fecha, hora_desde: "", hora_hasta: "", dias: "", referencia: "", instrucciones: "", chofer_id: "",
    zona_horaria: "America/Chicago", tipo_descarga: clave === "delivery" ? "live_unload" : "",
  };
}

export function etapasNuevas(fecha, tipo = "importacion") {
  const flujo = tipo === "exportacion" ? FLUJO_EXPORT : FLUJO_IMPORT;
  return ORDEN_ETAPAS.map((c) => {
    const e = etapaVacia(c, fecha);
    e.activa = flujo.includes(c) && c !== "yard_stop";
    if (tipo === "exportacion" && c === "pick_chassis") e.nombre = "Pickup Chassis";
    return e;
  });
}

// Cambia el tipo en una carga que todavía no empezó: conserva lo cargado en las etapas que los dos flujos comparten.
export function cambiarFlujo(etapas, tipo, fecha = "") {
  const nuevas = etapasNuevas(fecha, tipo);
  return nuevas.map((n) => {
    const v = etapas.find((e) => e.clave === n.clave);
    if (!v || !FLUJO_IMPORT.includes(n.clave) || !FLUJO_EXPORT.includes(n.clave)) return n;
    const nombreAuto = ["Pick up chassis", "Pickup Chassis", "Return chassis"].includes(v.nombre);
    return { ...v, activa: true, nombre: nombreAuto ? n.nombre : v.nombre };
  });
}

// Lo que dispatch puede editar de cada etapa, copiado de la carga que devolvió el servidor.
export function desdeCarga(carga) {
  return ORDEN_ETAPAS.map((clave) => {
    const s = (carga.etapas || []).find((e) => e.clave === clave);
    const base = etapaVacia(clave);
    if (!s) return { ...base, activa: false };
    const out = { ...base, activa: !!s.activa };
    CAMPOS.forEach((k) => {
      const v = s[k];
      out[k] = v == null ? base[k] : v;
    });
    return out;
  });
}

export function datosDesdeCarga(c) {
  return {
    load_id: c.load_id || "", tipo: c.tipo_operacion || "", tamano: c.tamano || "", contenedor: c.numero_contenedor || "",
    cliente_id: c.cliente_id || "", notas: c.notas || "",
  };
}

export const normContenedor = (s) => (s || "").replace(/[\s-]/g, "").toUpperCase();

// Cuerpo de POST /cargas y PUT /cargas/{id}. Una etapa desactivada solo manda su clave (el servidor no cambia nada).
export function armarPayload(datos, etapas) {
  return {
    load_id: (datos.load_id || "").trim(),
    numero_contenedor: datos.tipo === "exportacion" && esFlujoExport(etapas) ? "" : normContenedor(datos.contenedor),
    tipo_operacion: datos.tipo,
    tamano: datos.tamano,
    cliente_id: datos.cliente_id,
    notas: datos.notas || "",
    etapas: etapas.map((e) => {
      const d = { clave: e.clave };
      if (e.clave === "yard_stop") d.activa = !!e.activa;
      if (!e.activa) return d;
      CAMPOS.forEach((k) => {
        if (e.clave !== "delivery" && k === "tipo_descarga") return;
        d[k] = e[k];
      });
      return d;
    }),
  };
}

// Primer problema que impide guardar (o null). El servidor valida lo mismo.
export function validarCarga(datos, etapas, { legacy = false, idExiste = false } = {}) {
  if (!legacy && !(datos.load_id || "").trim()) return t("Escribe el ID de carga");
  if (idExiste) return t("Ya existe una carga con ese ID de carga");
  if (!datos.tipo) return t("Elige el tipo de operación");
  if (!datos.tamano) return t("Elige el tamaño del contenedor (20, 40, 40HC, 45 o 53)");
  const exportNuevo = datos.tipo === "exportacion" && esFlujoExport(etapas);
  if (!exportNuevo && !normContenedor(datos.contenedor)) return t("Escribe el número de contenedor");
  if (!datos.cliente_id) return t("Elige un cliente");
  const primera = etapas.find((e) => e.clave === (exportNuevo ? "empty_pickup" : "rail_pickup"));
  if (!legacy && !primera?.fecha) return exportNuevo ? t("Carga la fecha del Empty Container Pickup") : t("Carga la fecha del Rail pickup");
  for (const e of etapas) {
    if (!e.activa) continue;
    const n = e.nombre || ETAPA[e.clave].nombre;
    if (!legacy && ETAPA[e.clave].lugarObligatorio && !(e.lugar || "").trim()) return t("La etapa «{n}» necesita un lugar", { n });
    if (e.hora_desde && e.hora_hasta && e.hora_hasta < e.hora_desde) return t("«{n}»: la hora «hasta» es anterior a la hora «desde»", { n });
    if (e.zip && !/^\d{5}(-\d{4})?$/.test(e.zip.trim())) return t("«{n}»: el ZIP debe tener 5 dígitos", { n });
  }
  return null;
}

export function direccionTexto(e) {
  const estZip = [e.estado_us, e.zip].filter(Boolean).join(" ");
  return [e.calle, e.ciudad, estZip].filter(Boolean).join(", ");
}

// «mar 13 oct · lun-vie · 7:00 AM – 11:00 PM CT»
export function horarioTexto(e) {
  const partes = [];
  if (e.fecha) partes.push(formatFecha(e.fecha));
  if (e.dias) partes.push(e.dias);
  const z = siglaZona(e.zona_horaria);
  if (e.hora_desde && e.hora_hasta) partes.push(`${formatHoraInicio(e.hora_desde)} – ${formatHoraInicio(e.hora_hasta)} ${z}`);
  else if (e.hora_desde) partes.push(t("desde {hora}", { hora: `${formatHoraInicio(e.hora_desde)} ${z}` }));
  else if (e.hora_hasta) partes.push(t("hasta {hora}", { hora: `${formatHoraInicio(e.hora_hasta)} ${z}` }));
  return partes.join(" · ");
}

// Frase para el subtítulo: quién tiene las etapas activas.
export function resumenChoferes(etapas, nombres) {
  const act = (etapas || []).filter((e) => e.activa);
  const ids = [...new Set(act.map((e) => e.chofer_id).filter(Boolean))];
  if (ids.length === 0) return t("Sin chofer asignado");
  const nom = (id) => nombres[id] || act.find((e) => e.chofer_id === id)?.chofer_nombre || t("chofer");
  if (ids.length === 1 && act.every((e) => e.chofer_id === ids[0])) return t("Todo asignado a {v0}", { v0: nom(ids[0]) });
  return ids.length === 1 ? t("1 chofer en el recorrido") : t("{n} choferes en el recorrido", { n: ids.length });
}
